"use client";
import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Paper, Link } from "@mui/material";
import { Plus_Jakarta_Sans, Inter } from "next/font/google";
import { useTranslation } from "react-i18next";

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

const TableOfContents = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { threshold: 0.5 },
    );
    document.querySelectorAll("h1, h2, h3, h4").forEach((heading) => {
      observer.observe(heading);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const tocItems = [
    { id: "overview", titleKey: "articleOneContent.toc.overview", indent: 0 },
    { id: "top-7-ways", titleKey: "articleOneContent.toc.top7Ways", indent: 0 },
    {
      id: "employee-referral",
      titleKey: "articleOneContent.toc.employeeReferral",
      indent: 1,
    },
    {
      id: "quality-over-quantity",
      titleKey: "articleOneContent.toc.qualityOverQuantity",
      indent: 1,
    },
    {
      id: "automate-recruitment",
      titleKey: "articleOneContent.toc.automateRecruitment",
      indent: 1,
    },
    {
      id: "remote-interview",
      titleKey: "articleOneContent.toc.remoteInterview",
      indent: 1,
    },
    {
      id: "social-media",
      titleKey: "articleOneContent.toc.socialMedia",
      indent: 1,
    },
    {
      id: "talent-pool",
      titleKey: "articleOneContent.toc.talentPool",
      indent: 1,
    },
    {
      id: "talent-acquisitions",
      titleKey: "articleOneContent.toc.talentAcquisitions",
      indent: 1,
    },
    {
      id: "ending-notes",
      titleKey: "articleOneContent.toc.endingNotes",
      indent: 0,
    },
  ];

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        display: { xs: "none", sm: "none", md: "none", xl: "none" },
        right: isArabic ? "auto" : { xs: "5%", sm: "10%" },
        left: isArabic ? { xs: "5%", sm: "10%" } : "auto",
        top: { xs: "50%", sm: "66.6%" },
        transform: "translateY(-50%)",
        width: "280px",
        p: 2,
        maxHeight: "calc(80vh - 20%)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: PlusJakartaSans.style.fontFamily,
          fontWeight: "700",
          textAlign: isArabic ? "right" : "left",
        }}
      >
        {t("articleOneContent.toc.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {tocItems.map(({ id, titleKey, indent }) => (
          <Typography
            key={id}
            onClick={() => scrollToSection(id)}
            sx={{
              cursor: "pointer",
              pl: isArabic ? 0 : indent * 2,
              pr: isArabic ? indent * 2 : 0,
              color: activeId === id ? "primary.main" : "#4B5963",
              fontFamily: inter.style.fontFamily,
              fontSize: "14px",
              textAlign: isArabic ? "right" : "left",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(titleKey)}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const ArticleOneContent = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const paragraphSx = {
    mb: ".75%",
    fontFamily: inter.style.fontFamily,
    fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
    color: "#4B5963",
    fontWeight: "400",
    textAlign: isArabic ? "right" : "left",
    direction: isArabic ? "rtl" : "ltr",
  };

  const headingSx = {
    fontFamily: PlusJakartaSans.style.fontFamily,
    fontWeight: "700",
    fontSize: { xs: "24px", sm: "28px", md: "34px" },
    textAlign: isArabic ? "right" : "left",
    direction: isArabic ? "rtl" : "ltr",
  };

  const subHeadingSx = {
    fontFamily: PlusJakartaSans.style.fontFamily,
    fontWeight: "700",
    fontSize: { xs: "20px", sm: "24px", md: "34px" },
    textAlign: isArabic ? "right" : "left",
    direction: isArabic ? "rtl" : "ltr",
  };

  return (
    <Container maxWidth="xl" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      <TableOfContents />
      <Paper elevation={2} sx={{ my: 4, border: "none", boxShadow: "none" }}>
        {/* Overview */}
        <Typography
          id="overview"
          variant="h4"
          component="h1"
          gutterBottom
          sx={headingSx}
        >
          {t("articleOneContent.overview.heading")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleOneContent.overview.p1")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleOneContent.overview.p2")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleOneContent.overview.p3")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleOneContent.overview.p4")}
        </Typography>

        {/* Top 7 Ways */}
        <Typography
          id="top-7-ways"
          variant="h4"
          component="h2"
          gutterBottom
          sx={{ ...headingSx, mt: 4 }}
        >
          {t("articleOneContent.top7Ways.heading")}
        </Typography>

        {/* Employee Referral */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="employee-referral"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 2 }}
          >
            {t("articleOneContent.employeeReferral.heading")}
          </Typography>
          <Typography sx={{ ...paragraphSx, mt: "1%" }}>
            {t("articleOneContent.employeeReferral.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.employeeReferral.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.employeeReferral.p3")}
          </Typography>
        </Box>

        {/* Quality Over Quantity */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="quality-over-quantity"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 4 }}
          >
            {t("articleOneContent.qualityOverQuantity.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.qualityOverQuantity.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.qualityOverQuantity.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.qualityOverQuantity.p3")}
          </Typography>
        </Box>

        {/* Automate Recruitment */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="automate-recruitment"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 4 }}
          >
            {t("articleOneContent.automateRecruitment.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.automateRecruitment.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.automateRecruitment.p2start")}
            <Link
              href="https://yello.co/blog/recruitment-operations-statistics/"
              target="_blank"
              style={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleOneContent.automateRecruitment.p2link")}
            </Link>
            {t("articleOneContent.automateRecruitment.p2end")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.automateRecruitment.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.automateRecruitment.p4")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.automateRecruitment.p5")}
          </Typography>
        </Box>

        {/* Remote Interview */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="remote-interview"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 4 }}
          >
            {t("articleOneContent.remoteInterview.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.remoteInterview.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.remoteInterview.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.remoteInterview.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.remoteInterview.p4")}
          </Typography>
        </Box>

        {/* Social Media */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="social-media"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 4 }}
          >
            {t("articleOneContent.socialMedia.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.socialMedia.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.socialMedia.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.socialMedia.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.socialMedia.p4")}
          </Typography>
        </Box>

        {/* Talent Pool */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="talent-pool"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 4 }}
          >
            {t("articleOneContent.talentPool.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.talentPool.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.talentPool.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.talentPool.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.talentPool.p4")}
          </Typography>
        </Box>

        {/* Talent Acquisitions */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="talent-acquisitions"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...subHeadingSx, mt: 4 }}
          >
            {t("articleOneContent.talentAcquisitions.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.talentAcquisitions.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleOneContent.talentAcquisitions.p2")}
          </Typography>
        </Box>

        {/* Ending Notes */}
        <Typography
          id="ending-notes"
          variant="h4"
          component="h2"
          gutterBottom
          sx={{ ...headingSx, mt: 4 }}
        >
          {t("articleOneContent.endingNotes.heading")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleOneContent.endingNotes.p1")}
        </Typography>
      </Paper>
    </Container>
  );
};

export default ArticleOneContent;
